<template>
  <div v-if="loading || sessionLoading" class="flex items-center justify-center h-screen">
    <i class="pi pi-spin pi-spinner text-4xl text-primary"></i>
    <p class="ml-3 text-gray-600 text-lg">Resuming your session...</p>
  </div>

  <div v-else-if="!session" class="flex flex-col gap-4 items-center justify-center h-screen">
    <p class="text-gray-500 text-lg">No active session</p>
    <Button label="Back to Exams" icon="pi pi-arrow-left" @click="router.push('/student/cbt')" />
  </div>

  <!-- CBT live monitoring consent gate (premium feature; only renders for
       schools with cbt.live_monitoring enabled). Blocks the exam UI until
       the student explicitly accepts and the webcam producer is connected. -->
  <div
    v-else-if="needsMonitoringConsent"
    class="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-6"
  >
    <div class="max-w-lg w-full">
      <MonitoringConsentBanner
        @accept="acceptMonitoringConsent"
        @cancel="cancelMonitoringConsent"
      />
      <p v-if="monitoringStarting" class="mt-4 text-center text-white text-sm">
        <i class="pi pi-spin pi-spinner mr-2"></i>Starting webcam…
      </p>
      <p v-else-if="monitoringError" class="mt-4 text-center text-red-200 text-sm">
        {{ monitoringError }} — please allow camera access and try again.
      </p>
    </div>
  </div>

  <div v-else class="min-h-screen flex flex-col bg-gray-50">
    <Toast />

    <!-- Header -->
    <header class="bg-white shadow-sm px-6 py-4 flex items-center justify-between border-b border-gray-300">
      <div>
        <h1 class="text-xl font-semibold text-gray-800">{{ title }}</h1>
        <p class="text-sm text-gray-500">Status: {{ session.status }}</p>
      </div>

      <div class="flex items-center gap-6">
        <div class="flex items-center gap-2 bg-primary/10 px-4 py-2 rounded-md">
          <i class="pi pi-clock text-primary-700"></i>
          <span class="text-primary-700 font-medium">Time Left: {{ formattedTime }}</span>
        </div>

        <Button label="Submit Exam" icon="pi pi-send" class="bg-primary border-none hover:bg-primary/90"
          @click="showConfirmDialog = true" />
      </div>
    </header>

    <!-- Main Content -->
    <main class="flex flex-1 flex-col lg:flex-row gap-6 px-6 py-6">
      <!-- Question Section -->
      <section class="flex-1 bg-white shadow rounded-xl p-6 border border-gray-100">
        <div class="flex items-center justify-between mb-4">
          <h2 class="text-lg font-semibold text-gray-800">
            Question {{ currentQuestionIndex + 1 }} of {{ questions.length }}
          </h2>
          <span class="text-sm text-gray-500">
            Type: {{ currentQuestion?.question_type.replaceAll('_', ' ') }}
          </span>
        </div>

        <ProseMirrorEditor :model-value="toProseMirrorJSON(currentQuestion?.question)" :readonly="true"
          :useFloatingToolbar="true" />

        <!-- Question Images -->
        <div v-if="currentQuestion?.images?.length" class="flex gap-2 mt-4 overflow-x-auto pb-2">
          <Image v-for="(img, idx) in currentQuestion.images" :key="idx" :src="img.url" alt="Question Image" width="150"
            preview class="rounded-md border" />
        </div>

        <!-- Options -->
        <div class="mt-6 space-y-4">
          <template v-if="['MULTIPLE_CHOICE', 'SINGLE_CHOICE', 'TRUE_FALSE'].includes(
            currentQuestion?.question_type
          )">
            <label v-for="option in currentQuestion?.options || []" :key="option.option"
              class="flex flex-col gap-2 p-3 rounded-lg border border-gray-300 hover:bg-gray-50 transition cursor-pointer"
              :class="{
                'border-primary bg-primary/10':
                  currentQuestion &&
                  (
                    Array.isArray(answers[currentQuestion.id] ?? [])
                      ? (answers[currentQuestion.id] as string[])?.includes(option.id)
                      : answers[currentQuestion.id] === option.id
                  ),
              }">
              <div class="flex items-center gap-3">
                <input :type="currentQuestion?.question_type === 'MULTIPLE_CHOICE'
                  ? 'checkbox'
                  : 'radio'" :name="'question-' + currentQuestion.id" :value="option.id"
                  class="w-5 h-5 accent-blue-600 shrink-0" :checked="currentQuestion &&
                    (
                      Array.isArray(answers[currentQuestion.id] ?? [])
                        ? (answers[currentQuestion.id] as string[])?.includes(option.id)
                        : answers[currentQuestion.id] === option.id
                    )
                    " @change="toggleAnswer(currentQuestion.id, option.id)" />
                <div class="flex-1">
                  <ProseMirrorEditor :model-value="toProseMirrorJSON(option.option)" :readonly="true"
                    :useFloatingToolbar="true" />
                </div>
              </div>

              <!-- Option Images -->
              <div v-if="option.images?.length" class="flex gap-2 ml-8">
                <Image v-for="(img, i) in option.images" :key="i" :src="img.url" alt="Option Image" width="100" preview
                  class="rounded border" />
              </div>
            </label>
          </template>

          <!-- Fill in the blanks -->
          <template v-else-if="currentQuestion?.question_type === 'FILL_IN_THE_BLANKS'">
            <input type="text" v-model="answers[currentQuestion.id]" placeholder="Type your answer..."
              class="border rounded-lg w-full px-4 py-2 focus:ring-2 focus:ring-primary focus:outline-none"
              @input="currentQuestion && answers[currentQuestion.id] != null && queueAnswer(currentQuestion.id, answers[currentQuestion.id] as string)"
              @blur="flushPendingAnswers" />
          </template>

          <!-- Theory Questions -->
          <template v-else-if="currentQuestion?.question_type === 'THEORY'">
            <div class="border rounded-lg p-2">
              <ProseMirrorEditor :modelValue="(answers[currentQuestion.id] as object)" :useFloatingToolbar="true"
                placeholder="Type your answer here..."
                @update:modelValue="(val) => { answers[currentQuestion.id] = val; queueAnswer(currentQuestion.id, val); }"
                @blur="flushPendingAnswers" />
            </div>
          </template>
        </div>

        <!-- Navigation Buttons -->
        <div class="flex justify-between items-center mt-10">
          <Button label="Previous" icon="pi pi-angle-left" :disabled="currentQuestionIndex === 0" @click="prevQuestion"
            severity="secondary" />
          <Button :label="isLastQuestion ? 'Submit' : 'Next'"
            :icon="isLastQuestion ? 'pi pi-send' : 'pi pi-angle-right'" icon-pos="right" @click="handleNextOrSubmit"
            class="bg-primary hover:bg-primary/90 border-none" />
        </div>
      </section>

      <!-- Sidebar -->
      <aside class="lg:w-72 bg-white shadow rounded-xl border border-gray-100 p-5 h-fit">
        <h3 class="text-lg font-semibold text-gray-700 mb-4">Question Navigator</h3>

        <div class="grid grid-cols-5 gap-2">
          <button v-for="(q, idx) in questions" :key="q.id" @click="goToQuestion(Number(idx))"
            class="w-9 h-9 flex items-center justify-center rounded-md text-sm font-semibold transition-all" :class="[
              currentQuestionIndex === Number(idx)
                ? 'bg-primary text-white'
                : answers[q.id]
                  ? 'bg-primary/10 text-primary border border-primary'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200 border border-gray-300',
            ]">
            {{ Number(idx) + 1 }}
          </button>
        </div>
      </aside>
    </main>

    <!-- Submit Confirmation Dialog -->
    <Dialog v-model:visible="showConfirmDialog" header="Confirm Submission" modal :closable="false"
      class="w-[90%] md:w-[30rem]">
      <p class="text-gray-700 mb-6 leading-relaxed">
        {{ submitConfirmationText }} <br />
        <span class="text-sm text-gray-500">
          Once submitted, you cannot change your answers.
        </span>
      </p>

      <div class="flex justify-end gap-4">
        <Button label="Cancel" severity="secondary" @click="showConfirmDialog = false" />
        <Button label="Yes, Submit" icon="pi pi-check" severity="success" @click="handleConfirmSubmit" />
      </div>
    </Dialog>

    <!-- 🧾 Result Dialog -->
    <Dialog v-model:visible="showResultDialog" header="Your CBT Result" modal :closable="false"
      class="w-[90%] md:w-[30rem]">
      <div v-if="resultData" class="space-y-4 text-center">
        <template v-if="hasTheoryQuestions">
          <div class="flex flex-col items-center gap-2">
            <i class="pi pi-check-circle text-primary text-4xl mb-2"></i>
            <p class="text-lg font-semibold text-gray-800">
              Exam Submitted
            </p>
            <p class="text-gray-600">
              Your exam has been submitted for grading.
              Since it contains theory questions, your final score will be released after manual review.
            </p>
          </div>
        </template>
        <template v-else>
          <p class="text-lg font-semibold text-gray-800">
            Score: <span class="text-primary">{{ resultData.score }}</span>
          </p>
          <p class="text-gray-600">Status: <b>{{ resultData.status }}</b></p>
        </template>
        <p class="text-gray-500 text-sm">Completed at: {{ resultData.end_time }}</p>
      </div>

      <template #footer>
        <Button label="Close" icon="pi pi-check" @click="closeResultDialog" />
      </template>
    </Dialog>

    <!-- ⏰ TIME UP MODAL -->
    <Dialog
      v-model:visible="showTimeUpDialog"
      header="Time Up!"
      modal
      :closable="false"
      :dismissableMask="false"
      class="w-[90%] md:w-[28rem]"
    >
      <div class="text-center space-y-4">
        <i class="pi pi-exclamation-triangle text-red-500 text-5xl"></i>

        <p class="text-lg font-semibold text-gray-800">
          Your exam time has ended
        </p>

        <p class="text-gray-600">
          Your exam will be submitted automatically in
          <span class="font-bold text-red-600">
            {{ timeUpCountdown }} seconds
          </span>
        </p>

        <Button
          label="Submit Now"
          icon="pi pi-send"
          class="bg-red-600 hover:bg-red-700 border-none w-full"
          @click="forceTimeUpSubmit"
        />
      </div>
    </Dialog>

  </div>
</template>

<script setup lang="ts">
import { useToast } from 'primevue/usetoast';
import { useCBTSessionStore } from '@/store/cbtSession';

import { useCbtTestMutations } from '@/composables/mutations/cbt/useCbtTestMutations';
import ProseMirrorEditor from '@/components/common/ProseMirrorEditor.vue';
import { useSubmitTheoryAnswers } from '@/composables/mutations/useSubmitTheoryAnswers';
import type { StudentAnswerInput, StudentTheoryAnswerInput, Question } from '@/types/composition-functions';
import { apolloClient } from '@/apolloClient';
import { SubmitAnswersDocument, SubmitTheoryAnswersDocument } from '@/types/vue-apollo-helpers';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { useCBTTestSession } from '@/composables/queries/cbt/useCBTTestSession';
import { seededShuffle } from '@/composables/common/seededShuffle';
import {
  SAVE_PENDING_MESSAGE,
  SUBMIT_FAILED_MESSAGE,
  computeExamDeadline,
  dueTimeWarning,
  requeueObjectiveAnswers,
  requeueTheoryAnswers,
  secondsRemaining,
  submitConfirmationMessage,
  submitErrorMessage,
  summarizeAnswers,
  timeWarningMessage,
} from './examLogic';
import {
  MonitoringConsentBanner,
  useMonitoringFeatureGate,
  useStudentMonitoring,
  type StudentMonitoringHandle,
} from '@/features/cbt-monitoring';

dayjs.extend(utc);

const toast = useToast();
const router = useRouter();
const route = useRoute();
const sessionStore = useCBTSessionStore();

const { submitAnswers, submitCBTTest } = useCbtTestMutations();
const { submitTheoryAnswers } = useSubmitTheoryAnswers();
const session = computed(() => sessionStore.session);

const toProseMirrorJSON = (text: string) => {
  if (!text) return null;
  try {
    const parsed = JSON.parse(text);
    if (parsed && typeof parsed === 'object') {
      return parsed;
    }
  } catch {
    // treat as plain text
  }
  return {
    type: 'doc',
    content: [
      {
        type: 'paragraph',
        content: [{ type: 'text', text }],
      },
    ],
  };
};

const showConfirmDialog = ref(false);
const showResultDialog = ref(false);
const resultData = ref<{ score?: number; status?: string; end_time?: string } | null>(null);

const title = computed(() => session.value?.cbtTest?.title || '');
const duration = computed(() => session.value?.cbtTest?.duration || 0);
const allTestQuestions = computed(() => session.value?.cbtTest?.questions || []);
const rawQuestions = computed(() => {
  const selectedIds = session.value?.selected_question_ids;
  if (selectedIds && selectedIds.length > 0) {
    const idSet = new Set(selectedIds);
    return allTestQuestions.value.filter(q => idSet.has(q.id));
  }
  return allTestQuestions.value;
});
const shuffledQuestions = ref<Question[]>([]);
const questions = computed(() => shuffledQuestions.value.length ? shuffledQuestions.value : rawQuestions.value);
const showTimeUpDialog = ref(false);
const timeUpCountdown = ref(7);
let timeUpTimer: ReturnType<typeof setInterval> | null = null;

const currentQuestionIndex = ref(0);
const currentQuestion = computed(
  () => questions.value[currentQuestionIndex.value],
);
const isLastQuestion = computed(
  () => currentQuestionIndex.value === questions.value.length - 1,
);
const hasTheoryQuestions = computed(() =>
  questions.value.some((q) => q.question_type === 'THEORY'),
);
const answers = ref<Record<string, string | string[] | object | null>>({});

// 🔄 Session Recovery Logic
const loading = ref(false);

// ─── CBT live monitoring (premium feature, per-school) ──────────────────
// All monitoring state is no-op for schools without the feature enabled —
// `monitoringFeatureEnabled` resolves false and `needsMonitoringConsent`
// stays false, so the existing exam UI renders unchanged.
const { enabled: monitoringFeatureEnabled, loading: monitoringFeatureLoading } = useMonitoringFeatureGate();
const monitoring = useStudentMonitoring();
const monitoringHandle = ref<StudentMonitoringHandle | null>(null);
const monitoringStarting = ref(false);
const monitoringError = ref<string | null>(null);
// Flips to true once the exam has been submitted. Without this the consent
// gate re-appears after submit (stopMonitoring() nulls the handle, which
// looks identical to "haven't started yet" to the computed below) and the
// result dialog gets covered by the banner overlay.
const examSubmitted = ref(false);

const needsMonitoringConsent = computed(() =>
  !monitoringFeatureLoading.value
  && monitoringFeatureEnabled.value
  && !monitoringHandle.value
  && !!session.value?.id
  && !examSubmitted.value,
);

async function acceptMonitoringConsent() {
  if (!session.value?.id || monitoringStarting.value) return;
  monitoringError.value = null;
  monitoringStarting.value = true;
  try {
    monitoringHandle.value = await monitoring.start(String(session.value.id));
  } catch (err) {
    monitoringError.value = (err as Error).message || 'Failed to start monitoring';
  } finally {
    monitoringStarting.value = false;
  }
}

function cancelMonitoringConsent() {
  router.push('/student/cbt');
}

function stopMonitoring() {
  if (monitoringHandle.value) {
    monitoringHandle.value.stop();
    monitoringHandle.value = null;
  }
}

const sessionId = computed(() => {
  const id = route.params.id;
  return Array.isArray(id) ? id[0] : id;
});

const {
  load: loadSession,
  refetch,
  session: fetchedSession,
  loading: sessionLoading,
  error,
} = useCBTTestSession({
  autoFetch: !!sessionId.value,
  variables: computed(() => ({ id: sessionId.value })),
});

const triggerTimeUpModal = () => {
  if (showTimeUpDialog.value || isSubmitting.value) return;

  showTimeUpDialog.value = true;
  timeUpCountdown.value = 7;

  timeUpTimer = setInterval(async() => {
    timeUpCountdown.value--;

    if (timeUpCountdown.value <= 0) {
      clearInterval(timeUpTimer!);
      timeUpTimer = null;
      showTimeUpDialog.value = false;
      await submitExam(true); // 🔥 AUTO SUBMIT
    }
  }, 1000);
};

const forceTimeUpSubmit = () => {
  if (timeUpTimer) clearInterval(timeUpTimer);
  showTimeUpDialog.value = false;
  submitExam(true);
};

const initializeExam = () => {
  if (!session.value) return;

  // The deadline is fixed by the session start, so a refresh never adds time.
  examDeadline = computeExamDeadline(session.value.start_time, session.value.cbtTest?.duration);
  startTimer();

  session.value.progress?.forEach((p) => {
    if (!p.question || !p.selectedOption) return;
    const qId = p.question.id;
    const optId = p.selectedOption.id;
    const qType = p.question.question_type;

    if (qType === 'MULTIPLE_CHOICE') {
      const current = (answers.value[qId] as string[]) || [];
      if (!current.includes(optId)) {
        answers.value[qId] = [...current, optId];
      }
    } else {
      answers.value[qId] = optId;
    }
  });

  session.value.theoryQuestionAnswers?.forEach((t) => {
    if (t.question && t.student_answer) {
      answers.value[t.question.id] = t.student_answer;
    }
  });

  // Shuffle questions using session ID as seed — each student gets a unique
  // but deterministic order (stays the same on page refresh)
  if (rawQuestions.value.length > 0 && session.value.id) {
    shuffledQuestions.value = seededShuffle(rawQuestions.value, String(session.value.id));
  }
};

const timeLeft = ref(30);
let timer: ReturnType<typeof setInterval> | null = null;
let examDeadline = 0;
const shownTimeWarnings = new Set<number>();

const maybeWarnAboutTime = (secondsLeft: number) => {
  const threshold = dueTimeWarning(secondsLeft, shownTimeWarnings);
  if (threshold === null) return;
  shownTimeWarnings.add(threshold);
  toast.add({ severity: 'warn', summary: 'Time check', detail: timeWarningMessage(threshold), life: 8000 });
};

const startTimer = () => {
  if (timer) clearInterval(timer);
  
  const updateTimer = () => {
    timeLeft.value = secondsRemaining(examDeadline);
    maybeWarnAboutTime(timeLeft.value);

    if (timeLeft.value <= 0) {
      if (timer) {
        clearInterval(timer);
        timer = null;
      }
      triggerTimeUpModal();
    }
  };

  updateTimer();

  if (timeLeft.value > 0) {
    timer = setInterval(updateTimer, 1000);
  }
};

const formattedTime = computed(() => {
  // If the value is somehow invalid, gracefully handle to avoid NaN:NaN display
  if (isNaN(timeLeft.value) || timeLeft.value < 0) {
    return '00:00';
  }
  const m = Math.floor(timeLeft.value / 60);
  const s = timeLeft.value % 60;
  return `${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
});

const toggleAnswer = (qId: string, optId: string) => {
  const type = currentQuestion.value?.question_type;
  if (type === 'MULTIPLE_CHOICE') {
    const current = (answers.value[qId] as string[]) || [];
    const isSelected = !current.includes(optId);

    const updated = isSelected
      ? [...current, optId]
      : current.filter((o) => o !== optId);
    answers.value[qId] = updated;

    // Queue all remaining selected options so backend gets accurate state
    // Remove any previous pending entries for this question first
    pendingObjectiveAnswers.value = pendingObjectiveAnswers.value.filter(a => a.question_id !== qId);
    updated.forEach((selectedOptId) => {
      pendingObjectiveAnswers.value.push({
        question_id: qId,
        selected_option_id: String(selectedOptId),
      });
    });
  } else {
    answers.value[qId] = optId;
    queueAnswer(qId, optId);
  }
};

const pendingObjectiveAnswers = ref<StudentAnswerInput[]>([]);
const pendingTheoryAnswers = ref<StudentTheoryAnswerInput[]>([]);

// Use a strict manual debounce for theory to avoid recreating objects on every single keystroke.
let theoryDebounceTimer: ReturnType<typeof setTimeout> | null = null;
let theoryDebounceCallback: (() => void) | null = null;

// Drain any pending debounce immediately — call this before reading pendingTheoryAnswers
const drainTheoryDebounce = () => {
  if (theoryDebounceTimer) {
    clearTimeout(theoryDebounceTimer);
    theoryDebounceTimer = null;
  }
  if (theoryDebounceCallback) {
    theoryDebounceCallback();
    theoryDebounceCallback = null;
  }
};

const queueAnswer = (qId: string, value: string | object) => {
  if (!session.value || !value) return;

  const question = questions.value.find((q) => q.id === qId);
  if (!question) return;

  if (question.question_type === 'THEORY' || question.question_type === 'FILL_IN_THE_BLANKS') {
    if (theoryDebounceTimer) clearTimeout(theoryDebounceTimer);

    // Store the callback so drainTheoryDebounce can execute it synchronously
    theoryDebounceCallback = () => {
      const existingIndex = pendingTheoryAnswers.value.findIndex(a => a.question_id === qId);
      if (existingIndex > -1) {
        pendingTheoryAnswers.value[existingIndex]!.answer = value;
      } else {
        pendingTheoryAnswers.value.push({
          question_id: qId,
          cbt_test_session_id: session.value!.id,
          answer: value,
        });
      }
    };

    theoryDebounceTimer = setTimeout(() => {
      if (theoryDebounceCallback) {
        theoryDebounceCallback();
        theoryDebounceCallback = null;
      }
      theoryDebounceTimer = null;
    }, 500);
  } else {
    pendingObjectiveAnswers.value.push({
      question_id: qId,
      selected_option_id: String(value),
    });
  }
};

const flushPendingAnswers = async(): Promise<boolean> => {
  if (!session.value) return true;
  drainTheoryDebounce(); // Ensure any pending debounced theory answer is captured

  const objectives = [...pendingObjectiveAnswers.value];
  const theories = [...pendingTheoryAnswers.value];

  // Deduplicate by taking the exact current state from `answers.value` for any question ID that had changes
  const questionsToSync = Array.from(new Set(objectives.map(o => o.question_id)));
  const dedupedObjectives: typeof objectives = [];

  questionsToSync.forEach(qId => {
    const ans = answers.value[qId];
    if (Array.isArray(ans)) {
      ans.forEach(optId => {
        dedupedObjectives.push({ question_id: qId, selected_option_id: String(optId) });
      });
    } else if (ans) {
      dedupedObjectives.push({ question_id: qId, selected_option_id: String(ans) });
    }
  });

  pendingObjectiveAnswers.value = [];
  pendingTheoryAnswers.value = [];

  // Each save is checked on its own: submitAnswers resolves null on failure rather
  // than throwing, and theory saves reject. Anything that didn't save goes back in
  // the queue for the next flush instead of being dropped.
  const [objectivesSaved, theoriesSaved] = await Promise.all([
    dedupedObjectives.length > 0
      ? submitAnswers(session.value.id, dedupedObjectives).then(Boolean, () => false)
      : Promise.resolve(true),
    theories.length > 0
      ? submitTheoryAnswers({ input: theories }).then((res) => !!res && !res.errors?.length, () => false)
      : Promise.resolve(true),
  ]);

  if (!objectivesSaved) {
    pendingObjectiveAnswers.value = requeueObjectiveAnswers(dedupedObjectives, pendingObjectiveAnswers.value);
  }
  if (!theoriesSaved) {
    pendingTheoryAnswers.value = requeueTheoryAnswers(theories, pendingTheoryAnswers.value);
  }

  const allSaved = objectivesSaved && theoriesSaved;
  if (!allSaved && !savePendingWarned) {
    savePendingWarned = true;
    toast.add({ severity: 'warn', summary: 'Saving answers', detail: SAVE_PENDING_MESSAGE, life: 8000 });
  } else if (allSaved) {
    savePendingWarned = false;
  }
  return allSaved;
};

// Show the "still saving" warning once per outage, not on every 15s retry.
let savePendingWarned = false;

const isSubmitting = ref(false);

const handleSubmitFailure = (auto: boolean, detail: string) => {
  isSubmitting.value = false;
  toast.add({ severity: 'error', summary: 'Submission failed', detail });

  // If auto-submit failed, make sure the dialog is open and accessible
  // so the user can manually click "Submit Now" to retry submission
  if (auto) {
    showTimeUpDialog.value = true;
    timeUpCountdown.value = 0; // Stop the auto countdown visually
  } else {
    startTimer(); // Restart the timer if they are still taking the exam manually
  }
};

const submitExam = async(auto = false) => {
  if (isSubmitting.value || resultData.value) return;
  isSubmitting.value = true;

  if (timer) clearInterval(timer);
  // Theory / fill-in answers only reach the server through these saves (the final
  // submit carries objective answers only), so retry once and refuse to submit
  // while any are still unsaved rather than lose them.
  await flushPendingAnswers();
  if (pendingTheoryAnswers.value.length > 0) await flushPendingAnswers();
  if (pendingTheoryAnswers.value.length > 0) {
    handleSubmitFailure(auto, SUBMIT_FAILED_MESSAGE);
    return;
  }

  const standardAnswers: StudentAnswerInput[] = [];

  for (const [qId, ans] of Object.entries(answers.value)) {
    const question = questions.value.find((q) => q.id === qId);
    if (!question || !ans) continue;

    if (question.question_type !== 'THEORY' && question.question_type !== 'FILL_IN_THE_BLANKS') {
      if (Array.isArray(ans)) {
        ans.forEach((a) =>
          standardAnswers.push({ question_id: qId, selected_option_id: String(a) }),
        );
      } else if (typeof ans === 'string' || typeof ans === 'number') {
        standardAnswers.push({ question_id: qId, selected_option_id: String(ans) });
      }
    }
  }

  const payload = {
    cbt_test_session_id: session.value.id,
    answers: standardAnswers,
  };

  try {
    const result = await submitCBTTest(payload);

    if (result) {
      // Tear down the webcam producer + anti-cheat listeners + backend
      // monitoring session immediately on successful submit, before the
      // result dialog opens (no point holding the camera open while the
      // student reads their score). Flag examSubmitted FIRST so the
      // consent-gate computed doesn't re-fire when stopMonitoring() nulls
      // the handle.
      examSubmitted.value = true;
      stopMonitoring();
      resultData.value = result;
      showResultDialog.value = true;
    }
  } catch (e) {
    console.error('Exam submission failed', e);
    handleSubmitFailure(auto, submitErrorMessage(e));
  }
};

const handleNextOrSubmit = async() => {
  if (!isLastQuestion.value) flushPendingAnswers();
  if (isLastQuestion.value) showConfirmDialog.value = true;
  else currentQuestionIndex.value++;
};
const prevQuestion = () => {
  flushPendingAnswers();
  if (currentQuestionIndex.value) currentQuestionIndex.value--;
};
const goToQuestion = (i: number) => {
  flushPendingAnswers();
  currentQuestionIndex.value = i;
};

const submitConfirmationText = computed(() =>
  submitConfirmationMessage(summarizeAnswers(questions.value.map((q) => q.id), answers.value)),
);

const handleConfirmSubmit = async() => {
  showConfirmDialog.value = false;
  await submitExam(false);
};

const closeResultDialog = () => {
  showResultDialog.value = false;
  router.push('/student/cbt');
};

const isRefreshing = ref(!session.value);

watch(fetchedSession, (newSession) => {
  if (newSession) {
    // If we are currently showing the result dialog or in the process of submitting, don't redirect yet
    if (showResultDialog.value || isSubmitting.value) return;

    if (newSession.status === 'completed' || newSession.status === 'submitted') {
      toast.add({
        severity: 'info',
        summary: 'Exam Completed',
        detail: 'This exam has already been completed.',
        life: 3000,
      });
      router.push('/student/cbt');
      return;
    }

    sessionStore.setSession(newSession);
    initializeExam();

    if (isRefreshing.value) {
      toast.add({
        severity: 'warn',
        summary: 'Warning',
        detail: 'Refreshing the page is not allowed during the exam.',
        life: 5000,
      });
      isRefreshing.value = false;
    }
  }
});

let syncInterval: ReturnType<typeof setInterval> | null = null;

onMounted(async() => {
  // If session is already in store (normal navigation), just init
  if (session.value) {
    if (session.value.status === 'completed' || session.value.status === 'submitted') {
      router.push('/student/cbt');
      return;
    }
    initializeExam();
  } else {
    // If refreshing (isRefreshing is true), useCBTTestSession will autoFetch based on ID
    if (sessionId.value) {
      // Logic handled by useCBTTestSession autoFetch and watch
    } else {
      // Handle case where there is no ID?
    }
  }

  window.addEventListener('beforeunload', handleBeforeUnload);
  history.pushState(null, '', location.href);
  window.addEventListener('popstate', handlePopState);

  syncInterval = setInterval(() => {
    flushPendingAnswers();
  }, 15000);
});

const handleBeforeUnload = (e: BeforeUnloadEvent) => {
  // Fire mutations via the existing apolloClient (which already has auth, URI, headers configured)
  // The beforeunload confirmation dialog gives these requests time to complete
  drainTheoryDebounce(); // Capture any debounced theory answer before reading the queue
  const objectives = [...pendingObjectiveAnswers.value];
  const theories = [...pendingTheoryAnswers.value];

  // Exact same dedup logic as flushPendingAnswers to capture accurate final multi-choice states
  const questionsToSync = Array.from(new Set(objectives.map(o => o.question_id)));
  const dedupedObjectives: typeof objectives = [];

  questionsToSync.forEach(qId => {
    const ans = answers.value[qId];
    if (Array.isArray(ans)) {
      ans.forEach(optId => {
        dedupedObjectives.push({ question_id: qId, selected_option_id: String(optId) });
      });
    } else if (ans) {
      dedupedObjectives.push({ question_id: qId, selected_option_id: String(ans) });
    }
  });

  if (dedupedObjectives.length > 0 && session.value) {
    apolloClient.mutate({
      mutation: SubmitAnswersDocument,
      variables: { sessionId: session.value.id, answers: dedupedObjectives },
    }).catch(() => { /* best-effort */ });
  }

  // Ensure theories are also synced on tab close
  if (theories.length > 0) {
    apolloClient.mutate({
      mutation: SubmitTheoryAnswersDocument,
      variables: { input: theories },
    }).catch(() => { /* best-effort */ });
  }

  pendingObjectiveAnswers.value = [];
  pendingTheoryAnswers.value = [];

  e.preventDefault();
  e.returnValue = '';
};

const handlePopState = () => {
  history.pushState(null, '', location.href);
};

onUnmounted(() => {
  if (timer) clearInterval(timer);
  if (timeUpTimer) clearInterval(timeUpTimer);
  if (syncInterval) clearInterval(syncInterval);
  flushPendingAnswers();
  // Idempotent — also called from submit success; this catches navigations
  // away (back button, route change) that didn't go through submit.
  stopMonitoring();
  window.removeEventListener('beforeunload', handleBeforeUnload);
  window.removeEventListener('popstate', handlePopState);
});
</script>
